import React, { useCallback, useEffect, useRef } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { FastshiftRunbookSummary } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button-next/new';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives-next';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import { getFastshiftRunbook } from '@/state/actions';

import { useRunbooksAnalytics } from '../../hooks/use-runbooks-analytics';
import { messages } from '../messages';

import { RunbookCardHeader } from './runbook-card-header';
import { RunbookDetailContent } from './runbook-detail-content';

export type RunbookCardProps = {
	summary: FastshiftRunbookSummary;
	isExpanded: boolean;
	onToggle: () => void;
	supportTicketId: string;
};

const runbookCardStyles = xcss({
	border: `1px solid ${token('color.border')}`,
	borderRadius: 'border.radius.100',
});

const runbookHeaderStyles = xcss({
	paddingBlock: 'space.150',
	paddingInline: 'space.150',
});

const runbookBodyStyles = xcss({
	borderBlockStart: `1px solid ${token('color.border')}`,
	paddingBlock: 'space.150',
	paddingInline: 'space.150',
});

const detailLoadingStyles = xcss({
	paddingBlock: 'space.300',
	paddingInline: 'space.150',
});

const runbookDetailErrorStyles = xcss({
	paddingBlock: 'space.150',
});

const RunbookDetailLoadingState = () => {
	const intl = useIntl();

	return (
		<Inline alignBlock="center" alignInline="center" xcss={detailLoadingStyles}>
			<Spinner
				testId="spinner"
				interactionName="load"
				label={intl.formatMessage(messages.runbooksPanelLoadingRunbookDetails)}
				size="medium"
			/>
		</Inline>
	);
};

type RunbookDetailErrorStateProps = {
	onRetry: () => void;
};

const RunbookDetailErrorState: React.FC<RunbookDetailErrorStateProps> = ({ onRetry }) => (
	<Stack alignBlock="center" alignInline="center" space="space.025" xcss={runbookDetailErrorStyles}>
		<Heading size="xxsmall" as="h3">
			<FormattedMessage {...messages.runbooksPanelDetailErrorMessage} />
		</Heading>
		<Button appearance="subtle" spacing="compact" onClick={onRetry}>
			<Text size="small" color="color.link">
				<FormattedMessage {...messages.runbooksPanelRetryButton} />
			</Text>
		</Button>
	</Stack>
);

export const RunbookCard: React.FC<RunbookCardProps> = ({
	summary,
	isExpanded,
	onToggle,
	supportTicketId,
}) => {
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const runbookDetail = useFastshiftSelector(
		(state) => state.runbooks.detailsByRunbookId[summary.runbookId],
	);
	const {
		sendRunbookCardExpandToggleUIEvent,
		sendRunbookDetailsTrackEvent,
		sendRunbookOpenInMarathonLinkClickUIEvent,
	} = useRunbooksAnalytics();
	const hasFiredDetailRef = useRef(false);

	const fetchRunbookDetail = useCallback(() => {
		dispatch(
			getFastshiftRunbook({
				stargateClient,
				supportTicketId,
				runbookId: summary.runbookId,
			}),
		);
	}, [dispatch, stargateClient, supportTicketId, summary.runbookId]);

	const handleToggle = useCallback(() => {
		sendRunbookCardExpandToggleUIEvent(supportTicketId, summary.runbookId, !isExpanded);
		onToggle();
	}, [
		isExpanded,
		onToggle,
		sendRunbookCardExpandToggleUIEvent,
		summary.runbookId,
		supportTicketId,
	]);

	const handleOpenInMarathon = useCallback(() => {
		sendRunbookOpenInMarathonLinkClickUIEvent(supportTicketId, summary.runbookId);
	}, [sendRunbookOpenInMarathonLinkClickUIEvent, summary.runbookId, supportTicketId]);

	useEffect(() => {
		if (isExpanded && !runbookDetail) {
			fetchRunbookDetail();
		}
	}, [fetchRunbookDetail, isExpanded, runbookDetail]);

	useEffect(() => {
		if (runbookDetail?.pending) {
			hasFiredDetailRef.current = false;
			return;
		}

		if (hasFiredDetailRef.current || !isExpanded || !runbookDetail?.data) {
			return;
		}

		hasFiredDetailRef.current = sendRunbookDetailsTrackEvent({
			supportTicketId,
			runbookId: summary.runbookId,
			product: runbookDetail.data.product,
			stepCount: runbookDetail.data.steps.length,
		});
	}, [isExpanded, runbookDetail, sendRunbookDetailsTrackEvent, summary.runbookId, supportTicketId]);

	return (
		<Box xcss={runbookCardStyles} testId={`runbook-card-${summary.runbookId}`}>
			<Box xcss={runbookHeaderStyles}>
				<RunbookCardHeader
					runbookId={summary.runbookId}
					title={summary.title}
					isExpanded={isExpanded}
					onToggle={handleToggle}
					onOpenInMarathon={handleOpenInMarathon}
				/>
			</Box>
			{isExpanded && (
				<Box xcss={runbookBodyStyles}>
					{!runbookDetail && <RunbookDetailLoadingState />}
					{runbookDetail?.pending && <RunbookDetailLoadingState />}
					{runbookDetail?.error && <RunbookDetailErrorState onRetry={fetchRunbookDetail} />}
					{runbookDetail?.data && (
						<RunbookDetailContent runbook={runbookDetail.data} supportTicketId={supportTicketId} />
					)}
				</Box>
			)}
		</Box>
	);
};
